import React, { useEffect, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { Button } from "@mui/material";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import DownloadIcon from "@mui/icons-material/Download";
// import LinearProgress from "@mui/material/LinearProgress";
import { CustomPagination, CustomNoRowsOverlay } from "../SecondaryFunctions";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import { SearchablePDF } from "../Reports/Report Download/SearchablePDF";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormGroup from "@mui/material/FormGroup";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Select from "@mui/material/Select";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";

// 22 = Accounting
// 23 = Business, MBA
// 31 = Marketing
// 35 = School of Business
// 64 = Finance
// 83 = SEM
// 100 = Faculty of Business

const businessDepartments = [
	{ deptId: 22, name: "Accounting" },
	{ deptId: 23, name: "Business, MBA" },
	{ deptId: 31, name: "Marketing" },
	{ deptId: 35, name: "Business", alias: "School of Business" },
	{ deptId: 64, name: "Finance" },
	{ deptId: 83, name: "SEM" },
	{ deptId: 100, name: "Faculty of Business" },
];

// datagrid columns
const columns = [
	{
		field: "term",
		headerName: "TERM",
		// width: 100,
		flex: 1.5,
		align: "center",
		headerAlign: "center",
		renderCell: (params) => (
			<div className="flex items-center justify-center h-full">{params.value}</div>
		),
	},
	{
		field: "reqnum",
		headerName: "REQNUM",
		flex: 1.5,
		align: "center",
		headerAlign: "center",
		renderCell: (params) => (
			<div className="flex items-center justify-center h-full">{params.value}</div>
		),
	},
	{
		field: "classes",
		headerName: "CLASSES",
		flex: 1.5,
		align: "center",
		headerAlign: "center",
		renderCell: (params) => (
			<div className="flex items-center justify-center h-full">{params.value}</div>
		),
	},
	{
		field: "loaded",
		headerName: "LOADED",
		flex: 1.5,
		align: "center",
		headerAlign: "center",
		renderCell: (params) => (
			<div className="flex items-center justify-center h-full">{params.value}</div>
		),
	},
	{
		field: "username",
		headerName: "USER",
		flex: 2.5,
		align: "center",
		headerAlign: "center",
		renderCell: (params) => (
			<div className="flex items-center justify-center h-full">{params.value}</div>
		),
	},
	{
		field: "email",
		headerName: "EMAIL",
		flex: 2.5,
		align: "center",
		headerAlign: "center",
		renderCell: (params) => (
			<div className="flex items-center justify-center h-full">{params.value}</div>
		),
	},
	{
		field: "department",
		headerName: "DEPARTMENT",
		flex: 2.5,
		align: "center",
		headerAlign: "center",
		renderCell: (params) => (
			<div className="flex items-center justify-center h-full">{params.value}</div>
		),
	},
];

export default function ChairReportGeneration() {
	const [loadingBar, setLoadingBar] = useState(true); // To show/hide loading bar
	const [loaded, setLoaded] = useState(false);
	const [termsFetched, setTermsFetched] = useState(false);
	const [requisitionsFetched, setRequisitionsFetched] = useState(false);
	const [terms, setTerms] = useState([]);
	const [requisitions, setRequisitions] = useState([]);
	const [filteredReqnums, setFilteredReqnums] = useState([]);
	const [selectedTerm, setSelectedTerm] = useState(null);
	const [selectedReqnumFilters, setSelectedReqnumFilters] = useState([]);
	const [selectedReqnum, setSelectedReqnum] = useState([]);
	const [selectionModel, setSelectionModel] = useState({ type: "include", ids: new Set() });
	const [openWindow, setOpenWindow] = useState(false); // Control the visibility of a dialog window
	const [checked, setChecked] = useState([true, true, true, true]);
	const [pdfLoading, setPdfLoading] = useState(false);
	const [pdfGrouping, setPdfGrouping] = useState(1);
	const [reportFilename, setReportFilename] = useState("");
	const [pdfProgress, setPdfProgress] = useState({ current: 0, total: 0 });

	const getDeptDisplayName = (deptName) => {
		const dept = businessDepartments.find((d) => d.name === deptName);
		return dept?.alias ?? deptName;
	};

	const parseReqnumPaste = (text) => {
		if (!text) return [];
		return text
			.split(/[\s,]+/)
			.map((t) => t.trim())
			.filter(Boolean);
	};

	const buildDefaultFilename = (prefix) => {
		if (!selectedTerm) return "SPOT Reports.pdf";

		const descTerm = selectedTerm.split(" - ")[1] || selectedTerm;
		return prefix ? `${prefix} SPOT Report ${descTerm}.pdf` : `SPOT Report - ${descTerm}.pdf`;
	};

	const normalizePdfFilename = (name) => {
		const trimmed = (name || "").trim();
		if (!trimmed) return buildDefaultFilename();
		return trimmed.toLowerCase().endsWith(".pdf") ? trimmed : `${trimmed}.pdf`;
	};

	// NEW: fully controlled DataGrid filter model (column filters + quick filter)
	const [filterModel, setFilterModel] = useState({
		items: [],
		quickFilterValues: [],
	});

	const handleClickOpen = () => {
		if (!selectedReqnum?.length) return;

		const selectedSet = new Set(selectedReqnum);
		const selectedReqs = filteredReqnums.filter((r) => selectedSet.has(String(r.reqnum)));

		if (!selectedReqs.length) {
			setReportFilename(buildDefaultFilename());
			setOpenWindow(true);
			return;
		}

		const firstDeptRaw = selectedReqs[0]?.department;
		if (!firstDeptRaw) {
			setReportFilename(buildDefaultFilename());
			setOpenWindow(true);
			return;
		}

		const isSameDept = selectedReqs.every((r) => r.department === firstDeptRaw);
		if (!isSameDept) {
			setReportFilename(buildDefaultFilename());
			setOpenWindow(true);
			return;
		}

		const isBusinessDept = businessDepartments.some((d) => d.name === firstDeptRaw);

		// start from aliased display name
		const deptDisplay = getDeptDisplayName(firstDeptRaw);
		let prefix = deptDisplay;

		if (isBusinessDept) {
			const ccids = selectedReqs
				.map((r) => (r.email || "").split("@")[0].toLowerCase())
				.filter(Boolean);

			const firstCcid = ccids[0];
			const allSameUser = ccids.length > 0 && ccids.every((c) => c === firstCcid);

			if (allSameUser) {
				if (firstCcid.includes("mba")) prefix = `${deptDisplay} (MBA)`;
				else if (firstCcid.includes("bcom")) prefix = `${deptDisplay} (UGRD)`;
			}
		}

		setReportFilename(buildDefaultFilename(prefix));
		setOpenWindow(true);
	};

	const handleClose = () => {
		setOpenWindow(false);
	};

	const handleCheck0 = (event) => {
		setChecked([event.target.checked, checked[1], checked[2], checked[3]]);
	};

	const handleCheck1 = (event) => {
		setChecked([checked[0], event.target.checked, checked[2], checked[3]]);
	};

	const handleCheck2 = (event) => {
		setChecked([checked[0], checked[1], event.target.checked, checked[3]]);
	};

	const handleCheck3 = (event) => {
		setChecked([checked[0], checked[1], checked[2], event.target.checked]);
	};

	const handlePDFgrouping = (event) => {
		setPdfGrouping(event.target.value);
	};

	useEffect(() => {
		let url = process.env.REACT_APP_BACKEND_HOST;
		async function fetch_terms() {
			const response = await fetch(url.concat("/spot/terms_list"), {
				headers: { Authorization: `Token ${sessionStorage.token}` },
			});
			const json = await response.json();

			let apiData = [];
			for (let i = 0; i < json.length; i++) {
				apiData.push([json[i].term, json[i].desc_term]);
			}
			setTermsFetched(true);
			return apiData;
		}

		async function fetch_requistitions() {
			let termValue = selectedTerm.split(" - ")[0];
			const response = await fetch(url.concat("/spot/term_requisitions/", termValue, "/"), {
				headers: { Authorization: `Token ${sessionStorage.token}` },
			});
			const json = await response.json();

			let apiData = [];
			let idValue = 0;
			for (let i = 0; i < json.length; i++) {
				let entry = {
					id: idValue,
					term: termValue,
					reqnum: json[i].reqnum.toString(),
					classes: json[i].classes.toString(),
					loaded: json[i].loaded.toString(),
					email: json[i].userid.concat("@ualberta.ca"),
					username: json[i].username,
					department: json[i].department,
				};
				apiData.push(entry);
				idValue++;
			}
			setRequisitionsFetched(true);
			return apiData;
		}

		if (!termsFetched) {
			let apiData = [fetch_terms()];
			Promise.all(apiData).then((values) => {
				// let data = values[0]
				// 	.slice()
				// 	.sort((a, b) => b - a)
				// 	.map((item) => item.toString());
				// setTerms(data);
				// setSelectedTerm(data[0]);

				let term_list = values[0]
					.slice()
					.sort((a, b) => b[0] - a[0])
					.map((item) => item[0].toString().concat(" - ", item[1].toString()));
				setTerms(term_list);
				setSelectedTerm(term_list[0]);
				setTermsFetched(true);
			});
		}
		if (termsFetched && !requisitionsFetched && selectedTerm) {
			let apiData = [fetch_requistitions()];
			Promise.all(apiData).then((values) => {
				let data = values[0];
				setRequisitions(data);
				setFilteredReqnums(data);
			});

			// if (requisitions.length === 0) {
			// 	setSelectedTerm(terms[1]);
			// }
			// Mark the data as loaded to prevent repeated fetching
			setLoaded(true);
		}

		if (loaded && loadingBar) {
			setTimeout(() => {
				setLoadingBar(false);
			}, 3000);
		}
	}, [loaded, termsFetched, loadingBar, requisitionsFetched, selectedTerm]);

	const handleTermChange = (event, newValue) => {
		// when term changes, refetch and clear everything
		setRequisitionsFetched(false);
		setLoaded(false);
		setLoadingBar(true);
		setSelectedTerm(newValue);
		setSelectedReqnumFilters([]);
		setSelectionModel({ type: "include", ids: new Set() });
		setSelectedReqnum([]);
		setReportFilename("");
	};

	const handleReqnumChange = (event, newValue) => {
		// Update the selectedTerm state when the value changes
		setSelectedReqnumFilters(newValue);
		if (newValue.length > 0) {
			const filteredList = requisitions.filter((item) => newValue.includes(item.reqnum));
			setFilteredReqnums(filteredList);
		} else {
			setFilteredReqnums(requisitions);
		}
		setSelectionModel({ type: "include", ids: new Set() });
		setSelectedReqnum([]);

		// when a reqnum search/filter is applied, clear all grid filters
		setFilterModel({
			items: [],
			quickFilterValues: [],
		});
	};

	const handleReqnumPaste = (event) => {
		const pastedText = event.clipboardData?.getData("text") || "";
		const pastedReqnums = parseReqnumPaste(pastedText);
		if (pastedReqnums.length === 0) return;

		event.preventDefault();
		event.stopPropagation();

		const validReqnums = new Set(requisitions.map((r) => r.reqnum));
		const merged = Array.from(
			new Set([...selectedReqnumFilters, ...pastedReqnums.filter((req) => validReqnums.has(req))]),
		);

		handleReqnumChange(null, merged);
	};

	function select_requisitions(items) {
		let selected_items = [];
		for (let i = 0; i < items.length; i++) {
			let entry = filteredReqnums.filter((x) => x.id === items[i]);
			if (entry[0]) {
				selected_items.push(entry[0].reqnum.toString());
			}
		}
		setSelectedReqnum(selected_items);
	}

	const prefixPdfFilename = (prefix, baseFilename) => {
		const safePrefix = (prefix || "").trim();
		const base = normalizePdfFilename(baseFilename); // ensures .pdf

		if (!safePrefix) return base;

		// avoid double prefixing if user runs it twice
		const baseNoExt = base.replace(/\.pdf$/i, "");
		return `${safePrefix} - ${baseNoExt}.pdf`;
	};

	async function PrintReport() {
		function allFalse(arr) {
			for (let i = 0; i < arr.length; i++) {
				if (arr[i] === true) return false;
			}
			return true;
		}

		if (allFalse(checked)) return;

		const baseFilename = normalizePdfFilename(reportFilename);

		if (pdfGrouping === 1) {
			setPdfLoading(true);
			const selectedIDs = ["R", `${selectedReqnum}`];
			await SearchablePDF(selectedIDs, checked, baseFilename);
			setPdfLoading(false);
			return;
		}

		if (pdfGrouping === 2) {
			setPdfLoading(true);
			const url = process.env.REACT_APP_BACKEND_HOST;

			const response = await fetch(
				url.concat("/spot/get_req_ids/", selectedReqnum.join(","), "/"),
				{ headers: { Authorization: `Token ${sessionStorage.token}` } },
			);

			const selectedIDs = await response.json();

			const uniqueEmpIds = [...new Set(selectedIDs.map((e) => e.empid))];
			const totalInstructors = uniqueEmpIds.length;
			setPdfProgress({ current: 0, total: totalInstructors });

			for (let i = 0; i < uniqueEmpIds.length; i++) {
				const empid = uniqueEmpIds[i];
				setPdfProgress({ current: i + 1, total: totalInstructors });

				const instRows = selectedIDs.filter((item) => item.empid === empid);

				const selected_data = instRows.map((item) => item.req_id);

				const instName = instRows[0]?.inst_name || "Instructor";
				const perInstFilename = prefixPdfFilename(instName, baseFilename);

				await SearchablePDF(selected_data, checked, perInstFilename);
			}

			setPdfProgress({ current: 0, total: 0 });
			setPdfLoading(false);
		}
	}

	const handleSelectionModelChange = (newSelection) => {
		// MUI X v8 selection model is an object: { type: 'include'|'exclude', ids: Set<GridRowId> }
		setSelectionModel(newSelection);

		let selectedIds = [];
		if (newSelection?.type === "exclude") {
			const excludedIds = new Set(newSelection?.ids || []);
			selectedIds = filteredReqnums
				.filter((row) => !excludedIds.has(row.id))
				.map((row) => Number(row.id));
		} else if (newSelection?.type === "include") {
			selectedIds = Array.from(newSelection?.ids || []).map((id) => Number(id));
		} else if (Array.isArray(newSelection)) {
			// Back-compat if any grid still sends an array
			selectedIds = newSelection.map((id) => Number(id));
		}

		select_requisitions(selectedIds);
	};
	return (
		<div style={{ backgroundColor: "#f5f5f5" }}>
			<div className="max-w-7xl mx-auto py-3" style={{maxHeight: `calc(100vh - calc(11rem + 8px))`}}>
				<div className="text-center flex justify-center items-center pt-2 w-full">
					<Autocomplete
						value={selectedTerm}
						onChange={handleTermChange}
						size="small"
						options={terms}
						sx={{ width: "100%", paddingRight: "12px" }}
						renderInput={(params) => (
							<TextField
								sx={{ backgroundColor: "#fff" }}
								{...params}
								label="TERM"
								InputProps={{
									...params.InputProps,
									endAdornment: (
										<React.Fragment>
											{loadingBar ? <CircularProgress color="inherit" size={20} /> : null}
											{params.InputProps.endAdornment}
										</React.Fragment>
									),
								}}
							/>
						)}
					/>
					<Autocomplete
						multiple
						limitTags={7}
						onChange={handleReqnumChange}
						value={selectedReqnumFilters}
						size="small"
						options={requisitions.map((item) => item.reqnum)}
						disableCloseOnSelect
						sx={{ width: 750, paddingLeft: "12px" }}
						renderInput={(params) => (
							<TextField
								sx={{ backgroundColor: "#fff" }}
								{...params}
								label="REQNUMS"
								inputProps={{
									...params.inputProps,
									onPaste: handleReqnumPaste,
								}}
								InputProps={{
									...params.InputProps,
									endAdornment: (
										<React.Fragment>
											{loadingBar ? <CircularProgress color="inherit" size={20} /> : null}
											{params.InputProps.endAdornment}
										</React.Fragment>
									),
								}}
							/>
						)}
					/>
				</div>
				<div className="flex text-center justify-center items-center pt-2">
					<DataGrid
						key={selectedTerm} // remount grid when term changes (hard reset)
						rows={filteredReqnums}
						columns={columns}
						getRowHeight={() => "auto"}
						checkboxSelection
						density="compact"
						loading={loadingBar}
						getRowClassName={(params) =>
							params.row.classes !== params.row.loaded ? "highlight" : ""
						}
						pageSizeOptions={[100]}
						// selection (controlled)
						rowSelectionModel={selectionModel}
						onRowSelectionModelChange={handleSelectionModelChange}
						keepNonExistentRowsSelected={false}
						// filters (fully controlled)
						filterModel={filterModel}
						onFilterModelChange={(model) => {
							setFilterModel(model);
							setSelectionModel({ type: "include", ids: new Set() });
							setSelectedReqnum([]);
						}}
						// If you're using GridToolbarQuickFilter
						onQuickFilterValuesChange={() => {
							setSelectionModel({ type: "include", ids: new Set() });
							setSelectedReqnum([]);
						}}
						// slots (v6+ casing)
						slots={{
							pagination: CustomPagination,
							loadingOverlay: CircularProgress,
							noRowsOverlay: CustomNoRowsOverlay,
						}}
						sx={{
							height: "65vh",
							width: "100%",
							"& .highlight": { color: "red" },
						}}
					/>
				</div>
				<div
					style={{
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						marginTop: "15px",
						// outline: '2px solid black'
					}}
				>
					<Button
						style={{
							backgroundColor: "#275d38",
							width: "45%",
							maxWidth: "400px",
						}}
						variant="contained"
						endIcon={<DownloadIcon />}
						onClick={handleClickOpen}
					>
						<Typography style={{ fontSize: "16px" }}>PDF Download</Typography>
					</Button>
					<Dialog open={openWindow} onClose={handleClose} fullWidth={true} maxWidth={"lg"}>
						<DialogTitle sx={{ textAlign: "center", fontWeight: "bold" }}>SPOT REPORT</DialogTitle>
						{pdfLoading ? (
							<Box
								sx={{
									display: "flex",
									flexDirection: "column",
									justifyContent: "center",
									alignItems: "center",
									paddingBottom: "20px",
									gap: "12px",
								}}
							>
								<CircularProgress size={80} />
								{pdfProgress.total > 0 && (
									<Typography variant="body1">
										Generating instructor {pdfProgress.current} / {pdfProgress.total}...
									</Typography>
								)}
							</Box>
						) : (
							<div>
								<DialogContent>
									<Box
										sx={{
											display: "flex",
											flexDirection: "column",
											justifyContent: "center",
											alignItems: "center",
										}}
									>
										<TextField
											label="File name"
											fullWidth
											value={reportFilename}
											onChange={(e) => setReportFilename(e.target.value)}
											sx={{ marginBottom: "16px" }}
										/>
										<Typography>
											Select which elements you would like included in the PDF report:
										</Typography>
										<br></br>
										<FormGroup>
											<FormControlLabel
												control={<Checkbox checked={checked[0]} onChange={handleCheck0} />}
												label="Standard SPOT Questions"
											/>
											<FormControlLabel
												control={<Checkbox checked={checked[1]} onChange={handleCheck1} />}
												label="Supplementary Questions"
											/>
											<FormControlLabel
												control={<Checkbox checked={checked[2]} onChange={handleCheck2} />}
												label="Summary Data"
											/>
											<FormControlLabel
												control={<Checkbox checked={checked[3]} onChange={handleCheck3} />}
												label="Comments"
											/>
											<br></br>
											<FormControl variant="filled">
												<InputLabel>PDF Format</InputLabel>
												<Select value={pdfGrouping} label="PDF Format" onChange={handlePDFgrouping}>
													<MenuItem value={1}>Single PDF</MenuItem>
													<MenuItem value={2}>PDF per Instructor</MenuItem>
												</Select>
											</FormControl>
										</FormGroup>
									</Box>
								</DialogContent>

								<DialogActions sx={{ justifyContent: "center" }}>
									<Button onClick={handleClose}>Close</Button>
									<Button onClick={PrintReport}>Generate</Button>
								</DialogActions>
							</div>
						)}
					</Dialog>
				</div>
			</div>
		</div>
	);
}
